// plainJoin — VQE-Edit helpers for the non-FK JOIN path. When the user joins a
// table that has NO foreign-key (or loose-rel) edge to the FROM-side anchor,
// they pick the left/right ON columns explicitly (see `JoinOnColumnForm`).
// These pure helpers derive the new alias, choose sensible default columns, and
// format the `AddJoinClause` the form commits via `addJoinClause`.
//
// Real-FK / loose-rel joins keep their auto-derived ON via
// `addJoinFromCandidate` — this module is only the user-picks-columns path.

import type { SqlEngine } from '../../../../sql-grammar/model';
import { sameRelation } from '../../../../sql-resolver/service/snapshotRelations';
import { catalogIdentSql, catalogRelationSql } from '../../../../sql-resolver/suggest/sqlIdentifierText';
import type { JoinKind } from '../../../../sql-resolver/types/queryStructure';
import type { AddJoinClause } from '../../../../sql-resolver/types/structuralEdit';
import type { Column, Table } from '../../../../sql-resolver/types/schema';

// Short lowercase alias from a table name: first char + the char after each
// word boundary (`_ - . space`) + every uppercase char, lowercased. Mirrors the
// resolver's FK-candidate alias deriver (kept local so this VQE slice stays
// self-contained). Disambiguated against `used` with a numeric suffix ≥ 2.
export function derivePlainJoinAlias(tableName: string, used: ReadonlySet<string>): string {
  const chars: string[] = [];
  let boundary = true;
  for (const c of tableName) {
    if (c === '_' || c === '-' || c === '.' || c === ' ') {
      boundary = true;
      continue;
    }
    const isUpper = c >= 'A' && c <= 'Z';
    if (boundary || isUpper) chars.push(c.toLowerCase());
    boundary = false;
  }
  let base = chars.join('');
  if (!base) {
    const alpha = [...tableName].find((c) => /[A-Za-z]/.test(c));
    base = alpha ? alpha.toLowerCase() : 't';
  }
  const lowerUsed = new Set([...used].map((a) => a.toLowerCase()));
  if (!lowerUsed.has(base)) return base;
  let i = 2;
  while (lowerUsed.has(`${base}${i}`)) i++;
  return `${base}${i}`;
}

// Confident default ON columns for the manual-join picker. Tries, in order:
//   1) a left column whose real FK references a column on the right side;
//   2) a left column named `<rightTable>Id` / `<rightTable>_id` when the right
//      table has a single PK column.
// Naive same-name matching is intentionally NOT used (e.g. Status = Status is a
// nonsensical join). Returns `undefined` when nothing confident is found so the
// form starts with no preselection.
export function pickSmartJoinColumns(
  leftColumns: readonly Column[],
  rightTable: Table,
  rightColumns: readonly Column[],
): { readonly leftColumn: Column; readonly rightColumn: string } | undefined {
  if (leftColumns.length === 0 || rightColumns.length === 0) return undefined;
  const pks = rightColumns.filter((c) => c.isPrimaryKey);
  const pk = pks.length === 1 ? pks[0] : undefined;

  // 1) Left FK whose foreignKeyRef column name matches a right column — gated
  // on the FK's referenced TABLE actually being the table being joined
  // (`foreignKeyRefTable`, from `FkEdge.toTable` — PARITY-FK-01). Without this
  // gate, a left FK to some unrelated table (e.g. `orders.customer_id →
  // customers.id`) would false-match any right table that merely happens to
  // have a same-named column (`id` is a near-universal PK name), pre-filling
  // a nonsensical ON clause. Compared as relations (`sameRelation`: name, and
  // schema when both sides carry one), so an FK to `mia_s1.cats` does not
  // pre-fill a join to `mia_s2.cats`, while engines that don't carry a schema
  // on one side still match by name.
  for (const l of leftColumns) {
    if (!l.isForeignKey || !l.foreignKeyRef) continue;
    if (!l.foreignKeyRefTable || !sameRelation(l.foreignKeyRefTable, rightTable.qualified)) continue;
    const refFolded = l.foreignKeyRef.foldedName;
    const match = rightColumns.find((r) => r.qualified.foldedName === refFolded);
    if (match) return { leftColumn: l, rightColumn: match.qualified.name };
  }

  // 2) Name heuristic against the single right PK.
  if (pk) {
    const t = rightTable.qualified.foldedName;
    const candidates = new Set([`${t}id`, `${t}_id`]);
    for (const l of leftColumns) {
      if (candidates.has(l.qualified.foldedName)) {
        return { leftColumn: l, rightColumn: pk.qualified.name };
      }
    }
  }
  return undefined;
}

export function buildPlainJoinClause(args: {
  readonly rightTable: Table;
  readonly rightAlias: string;
  /** The left side of the ON equality as SQL (a picker item's `sqlRef`), e.g.
   *  `customer.store_id`, `s.store_id`, `"order items".product_id`. */
  readonly leftQualified: string;
  /** The right column's catalog name — delimited here when it must be. */
  readonly rightColumn: string;
  readonly engine: SqlEngine;
  readonly kind?: JoinKind;
}): AddJoinClause {
  const relation = catalogRelationSql(args.rightTable.qualified, args.engine);
  return {
    kind: args.kind ?? 'inner',
    targetText: `${relation} ${args.rightAlias}`,
    // `<in-scope table>.<col> = <new alias>.<col>`. The predicate is symmetric;
    // the user can refine it afterward via the inline ON editor.
    onText: `${args.leftQualified} = ${args.rightAlias}.${catalogIdentSql(args.rightColumn, args.engine)}`,
  };
}
